import { BaseEditor } from "@/libs/map-draw/controllers";
import { getEditorRequestConfig, jsonToGeojson, debounce } from "@/utils";
import { clearAllHelpers } from "@/libs/map-draw/helpers";
import { trafficCountsLayersConfig } from "../configs";

const tcLayers = ["general_count_points", "count_points"];

export class EditorController extends BaseEditor {
  constructor(parent) {
    super(parent);

    this.parent = parent;
    this.state = parent.editorState;
    this.handlers = {};
    this.toggleHandlers = {};
  }

  // toggling mode
  toggleOn() {
    const { children } = this.parent.$store.state.trafficCount.model;

    this.state.modelLayers = children
      .filter(l => tcLayers.indexOf(l.datatype) > -1)
      .map(({ id, datatype, geom_type, name }) => ({
        id,
        datatype,
        geom_type,
        name,
        visible: false,
        loading: false
      }));
    this.state.modelLayers.forEach(l => {
      this.toggleVisibleModelLayer(l.id);
    });
  }
  toggleOff() {
    this.clearVisibleLayers();
    this.closeButtonHandler();
  }

  // common helpers
  clearEditedFeatures() {
    this.clearEditorData();
    this.draw.deleteAll();
    if (this.loadLayerData) this.mapgl.off("moveend", this.loadLayerData);
    this.state.id = "";
    this.state.modelLayers = [];
    this.toggleEditorMode("edit");
  }
  clearVisibleLayers() {
    const { id, modelLayers } = this.state;
    const visibleLayers = modelLayers.filter(l => l.id !== id && l.visible);

    visibleLayers.forEach(l => this.toggleVisibleModelLayer(l.id));
  }

  // mode helpers
  changeModeToCreate() {
    const mode = `draw_general_count_points`;
    this.draw.changeMode(mode);
    this.state.mode = "create";
  }
  toggleEditorMode(newMode) {
    const { mode, id, modelLayers } = this.state;
    if (newMode === mode) return;
    const createMode = `draw_general_count_points`;
    const gcpLayer = modelLayers.find(
      l => l.datatype === "general_count_points"
    );

    switch (newMode) {
      case "create":
        if (!id) this.toggleEditingModelLayer(gcpLayer.id);
        this.draw.changeMode(createMode);
        break;
      case "edit":
        this.draw.changeMode("traffic_counts_simple_select");
        break;
    }

    this.clearMultiOptions();
    clearAllHelpers(this.mapgl);
    this.state.mode = newMode;
  }

  // save and close handlers
  async saveData() {
    await this.requestForSaving();
    this.clearEditorData();
    this.clearMultiOptions();
    this.loadLayerData();
    this.state.modelLayers
      .filter(
        l => l.visible && l.datatype !== "nodes" && l.datatype !== "links"
      )
      .forEach(l => this.handlers[l.id]());
  }

  closeButtonHandler() {
    if (this.state.history.length) {
      const successCb = () => {
        this.requestForSaving();
        this.clearEditedFeatures();
      };
      const errorCb = action => {
        if (action === "cancel") {
          this.clearEditedFeatures();
        }
      };
      this.showConfirmModal(successCb, errorCb);
    } else this.clearEditedFeatures();
  }

  clearMultiOptions() {
    this.state.multi = false;
    this.state.multiIds = [];
  }

  // layers helpers
  toggleVisibleModelLayer(id) {
    const { ids } = this.parent.$store.state.trafficCount.model;
    const { modelLayers } = this.state;
    const layer = modelLayers.find(l => l.id === id);
    const { datatype } = layer;
    if (layer.loading) return;

    if (layer.visible) {
      if (this.mapgl.getLayer(id)) this.mapgl.removeLayer(id);
      if (this.mapgl.getSource(id)) this.mapgl.removeSource(id);
      this.mapgl.off("moveend", this.handlers[id]);
      this.mapgl.off("click", id, this.toggleHandlers[id]);
      this.mapgl.off("mousemove", id, this.visibleLayerMoveHanler);
      this.mapgl.off("mouseleave", id, this.visibleLayerLeaveHandler);
      delete this.handlers[id];
      delete this.toggleHandlers[id];
      layer.visible = false;
    } else {
      const loadModelLayer = async () => {
        layer.loading = true;
        const zoom = this.mapgl.getZoom();
        const config = getEditorRequestConfig(this.map);
        if (datatype === "nodes")
          config.include = {
            from_links: { only: ["id"] }
          };

        try {
          const url = `objectInfo/${id}?config=${JSON.stringify(
            config
          )}&zoom=${zoom}`;
          const { data } = await this.map.$store.dispatch("REQUEST", {
            url
          });
          const geojson = jsonToGeojson(Object.keys(data).map(id => data[id]));

          if (!this.mapgl.getLayer(id)) {
            const options = {
              id,
              source: {
                type: "geojson",
                data: geojson
              },
              ...trafficCountsLayersConfig[datatype]
            };
            if (this.mapgl.getLayer(ids["nodes"]))
              this.mapgl.addLayer(options, ids["nodes"]);
            else if (this.mapgl.getLayer("gl-draw-polygon-fill-inactive.cold"))
              this.mapgl.addLayer(
                options,
                "gl-draw-polygon-fill-inactive.cold"
              );
            else this.mapgl.addLayer(options);
          } else {
            this.mapgl.getSource(id).setData(geojson);
          }

          layer.loading = false;
        } catch (error) {
          console.log(error);
          layer.loading = false;
        }
      };

      this.handlers[id] = debounce(loadModelLayer, 400);
      this.handlers[id]();
      this.mapgl.on("moveend", this.handlers[id]);

      if (datatype === "general_count_points" || datatype === "count_points") {
        this.toggleHandlers[id] = this.visibleLayerClickHandler(id);
        this.mapgl.on("click", id, this.toggleHandlers[id]);
        this.mapgl.on("mousemove", id, this.visibleLayerMoveHanler);
        this.mapgl.on("mouseleave", id, this.visibleLayerLeaveHandler);
      }

      layer.visible = true;
    }
  }

  // visible layers helpers handlers
  visibleLayerClickHandler = id => {
    return () => this.toggleEditingModelLayer(id);
  };
  visibleLayerMoveHanler = () => {
    this.mapgl.getCanvas().style.cursor = "pointer";
  };
  visibleLayerLeaveHandler = () => {
    this.mapgl.getCanvas().style.cursor = "";
  };

  // toggle currently editing layer
  toggleEditingModelLayer(layerId) {
    const { id, modelLayers } = this.state;
    if (layerId === id) return;

    const layer = modelLayers.find(l => l.id === layerId);
    const { visible, geom_type, datatype } = layer;

    if (datatype === "nodes" || datatype === "links") return;
    if (visible) {
      this.toggleVisibleModelLayer(layerId);
    }

    const setNewEditingLayer = () => {
      this.state.id = layerId;
      this.state.geom_type = datatype || geom_type;
      clearAllHelpers(this.mapgl);
      this.setEditorHandler();
      if (id) this.toggleVisibleModelLayer(id);
      if (datatype === "count_points") this.toggleEditorMode("edit");
    };
    const successCb = async () => {
      await this.requestForSaving();
      setNewEditingLayer();
    };
    const errorCb = action => {
      if (action === "cancel") setNewEditingLayer();
    };

    if (this.state.history.length) {
      this.showConfirmModal(successCb, errorCb);
    } else setNewEditingLayer();
  }

  // traffic counts handlers
  setEditorHandler() {
    const loadLayerData = async () => {
      const { id, geom_type, isDrawing, mode } = this.state;
      if (mode === "fill") return;
      if (!id) return;
      const selected = this.draw.getSelectedIds();
      if (isDrawing) return;

      const zoom = this.mapgl.getZoom();
      const config = getEditorRequestConfig(this.map, geom_type);
      let url;

      if (geom_type === "general_count_points") {
        config.only = undefined;
        url = `gcp/${id}?format=with_measure_count&config=${JSON.stringify(
          config
        )}&zoom=${zoom}`;
      } else {
        config.only = ["id", "geom", "link_id"];
        url = `objectInfo/${id}?config=${JSON.stringify(config)}&zoom=${zoom}`;
      }

      try {
        this.map.mapLoading = true;
        const { data } = await this.map.$store.dispatch("REQUEST", { url });
        const geojson = jsonToGeojson(Object.keys(data).map(id => data[id]));
        const editedFeatures = [
          ...this.created,
          ...this.updated,
          ...this.state.multiFeatures.map(f => f.toGeoJSON())
        ];
        geojson.features = geojson.features
          .filter(f => {
            return (
              !this.deleted.find(
                item => item.properties.id === f.properties.id
              ) &&
              !this.updated.find(item => item.properties.id === f.properties.id)
            );
          })
          .map(f => ({ ...f, id: f.properties.id }));
        // if (!enabled) return;
        let features = [...editedFeatures, ...geojson.features];

        this.draw.set({
          type: "FeatureCollection",
          features
        });
        if (this.state.mode === "create") this.changeModeToCreate();
        else
          this.draw.changeMode("traffic_counts_simple_select", {
            featureIds: selected
          });
        this.map.mapLoading = false;
      } catch (error) {
        console.log(error);
        this.map.mapLoading = false;
      }
    };
    if (this.loadLayerData) this.mapgl.off("moveend", this.loadLayerData);
    this.loadLayerData = debounce(loadLayerData, 400);
    this.clearEditorData();
    this.loadLayerData();
    this.mapgl.on("moveend", this.loadLayerData);
  }
}
